import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık'
];

const WEEKDAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

// Pazartesi'yi haftanın ilk günü kabul eden ofset (JS getDay: 0=Pazar).
const getMondayOffset = (jsDay) => (jsDay + 6) % 7;

// Etkinlik kategorisi: "tatil" tipi milli bayram/özel gün (kırmızı), geri
// kalan tüm etkinlik türleri (toplantı, eğitim, sosyal, proje, genel)
// kurumsal etkinlik (turuncu) olarak kabul edilir (bkz. Faz 6).
const isMilliBayram = (event) => event.type === 'tatil';

const DashboardCalendar = () => {
  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [calendarData, setCalendarData] = useState({ events: [], birthdays: [] });
  const [loading, setLoading] = useState(true);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  useEffect(() => {
    let active = true;

    const fetchMonth = async () => {
      try {
        setLoading(true);
        const response = await api.getCalendarMonth(year, month);
        if (!active) {
          return;
        }
        setCalendarData({
          events: response?.calendar?.events || [],
          birthdays: response?.calendar?.birthdays || []
        });
      } catch (error) {
        console.error('Takvim verileri yüklenemedi:', error);
        if (active) {
          setCalendarData({ events: [], birthdays: [] });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchMonth();
    return () => {
      active = false;
    };
  }, [year, month]);

  // Ay değişince geçerli bir gün seç (bugün ya da ayın 1'i).
  useEffect(() => {
    setSelectedDay(isCurrentMonth ? today.getDate() : 1);
  }, [isCurrentMonth, today]);

  const eventsByDay = useMemo(() => {
    const map = {};
    (calendarData.events || []).forEach((event) => {
      const day = new Date(event.startDate).getDate();
      if (!map[day]) {
        map[day] = [];
      }
      map[day].push(event);
    });
    return map;
  }, [calendarData.events]);

  const birthdaysByDay = useMemo(() => {
    const map = {};
    (calendarData.birthdays || []).forEach((person) => {
      if (!map[person.day]) {
        map[person.day] = [];
      }
      map[person.day].push(person);
    });
    return map;
  }, [calendarData.birthdays]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = getMondayOffset(new Date(year, month, 1).getDay());

  const cells = useMemo(() => {
    const result = [];
    for (let i = 0; i < leadingBlanks; i += 1) {
      result.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      result.push(day);
    }
    return result;
  }, [leadingBlanks, daysInMonth]);

  const goToMonth = useCallback((delta) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }, []);

  const selectedEvents = eventsByDay[selectedDay] || [];
  const selectedBirthdays = birthdaysByDay[selectedDay] || [];

  return (
    <div className="dashboard-calendar">
      <div className="dashboard-calendar-header">
        <button
          type="button"
          className="dashboard-calendar-nav"
          onClick={() => goToMonth(-1)}
          aria-label="Önceki ay"
        >
          <i className="bx bx-chevron-left" aria-hidden="true"></i>
        </button>
        <span className="dashboard-calendar-title">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          className="dashboard-calendar-nav"
          onClick={() => goToMonth(1)}
          aria-label="Sonraki ay"
        >
          <i className="bx bx-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div className="dashboard-calendar-layout">
        <div className="dashboard-calendar-main">
          <div className="dashboard-calendar-weekdays">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div className="dashboard-calendar-grid">
            {cells.map((day, index) => {
              if (day === null) {
                return <span key={`blank-${index}`} className="dashboard-calendar-cell is-blank" />;
              }

              const dayEvents = eventsByDay[day] || [];
              const hasMilliBayram = dayEvents.some(isMilliBayram);
              const hasKurumsalEtkinlik = dayEvents.some((event) => !isMilliBayram(event));
              const hasBirthday = Boolean(birthdaysByDay[day]);
              const isToday = isCurrentMonth && day === today.getDate();
              const isSelected = day === selectedDay;

              return (
                <button
                  key={day}
                  type="button"
                  className={`dashboard-calendar-cell${isToday ? ' is-today' : ''}${
                    isSelected ? ' is-selected' : ''
                  }`}
                  onClick={() => setSelectedDay(day)}
                  aria-label={`${day} ${MONTH_NAMES[month]}${
                    hasMilliBayram ? ', milli bayram/özel gün var' : ''
                  }${hasKurumsalEtkinlik ? ', kurumsal etkinlik var' : ''}${
                    hasBirthday ? ', doğum günü var' : ''
                  }`}
                >
                  <span>{day}</span>
                  {(hasMilliBayram || hasKurumsalEtkinlik || hasBirthday) && (
                    <span className="dashboard-calendar-dots">
                      {hasMilliBayram && <span className="dashboard-calendar-dot is-milli-bayram" />}
                      {hasKurumsalEtkinlik && <span className="dashboard-calendar-dot is-kurumsal" />}
                      {hasBirthday && <span className="dashboard-calendar-dot is-birthday" />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="dashboard-calendar-details">
          <h6>
            {selectedDay} {MONTH_NAMES[month]}
          </h6>

          {loading ? (
            <p className="dashboard-calendar-empty">Yükleniyor…</p>
          ) : selectedEvents.length === 0 && selectedBirthdays.length === 0 ? (
            <p className="dashboard-calendar-empty">Bu gün için kayıt yok.</p>
          ) : (
            <div className="dashboard-calendar-detail-list">
              {selectedEvents.map((event) => (
                <div
                  key={event.id}
                  className={`dashboard-calendar-detail-item ${
                    isMilliBayram(event) ? 'is-milli-bayram' : 'is-kurumsal'
                  }`}
                >
                  <i className="bx bx-calendar-event" aria-hidden="true"></i>
                  <div>
                    <strong>{event.title}</strong>
                    {(event.startTime || event.location) && (
                      <small>
                        {[event.startTime, event.location].filter(Boolean).join(' · ')}
                      </small>
                    )}
                  </div>
                </div>
              ))}
              {selectedBirthdays.map((person) => (
                <div key={person.id} className="dashboard-calendar-detail-item is-birthday">
                  <i className="bx bxs-cake" aria-hidden="true"></i>
                  <div>
                    <strong>
                      {person.firstName} {person.lastName}
                    </strong>
                    {person.department && <small>{person.department} · Doğum günü</small>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardCalendar;
